import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import {
  updateBuyerDetails,
  getBuyBoxMatchedProperties,
  updateBuyBoxPropertyMatches,
} from '../../reducers/Buyer/actions';
import ContentHeader from '../../components/ContentHeader';
import { Page } from '../../components/layout';
import AddBuyboxForm from './Components/AddBuyboxForm';
import BuyboxList from './Components/BuyboxList';
import {
  BUYBOX_VARIANT_ONBOARDING,
  BUYBOX_VARIANT_PAGE,
  DEFAULT_BUYBOX_PARAMS,
} from './constants';

import './index.scss';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { reportScreen } from '../../services/analyticsService';
import { findAddedAndRemovedAreas } from './service';
import { WhiteLabelTrainingModal } from '../../containers/layout/Components/WhiteLabelTrainingModal';

const BuyBoxPage = (props) => {
  useDocumentTitle('Buy Box');
  const { variant = BUYBOX_VARIANT_PAGE } = props;
  const {
    isLoading,
    isFetchingBuyboxMatchedProperties,
    buyBoxPropertyMatch,
    isMatchingCountShown,
  } = useSelector(({ BuyerReducer }) => BuyerReducer);
  const { userDetails, organization } = useSelector(
    ({ AuthReducer }) => AuthReducer,
  );
  const isMarketplace = Boolean(
    userDetails?.defaultOrganization?.marketplacePartner,
  );
  const [selectedBuyBox, setSelectedBuyBox] = useState();
  const [buyBoxList, setBuyBoxList] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [params, setParams] = useState();
  const [changedParams, setChangedParams] = useState({});

  const dispatch = useDispatch();

  const userId = userDetails._id;

  /**
   * An Helper function to call API and save buyBox list in database
   * @see reducer/Buyer/actions/updateBuyerDetails
   * @param {*} requestParams  {buyBoxList: [],requestedUserId: string,}
   */
  const saveOrganization = (requestParams) => {
    dispatch(updateBuyerDetails(requestParams));
  };

  const onCreateCriteria = () => {
    const newBuyBox = { ...DEFAULT_BUYBOX_PARAMS };
    const updatedBuyBoxList = [newBuyBox, ...buyBoxList];
    setBuyBoxList(updatedBuyBoxList);
    saveOrganization({
      buyBoxList: updatedBuyBoxList,
      requestedUserId: userId,
    });
    setIsUpdateMode(false);
  };

  useEffect(() => {
    if (organization?.buyBoxList.length === 0) {
      onCreateCriteria();
    }
    reportScreen('buybox_page');
  }, []);

  /**
   * Whenever we update the buy box in the db make sure to refresh the updated array here
   */
  useEffect(() => {
    const list = organization?.buyBoxList || [];
    setBuyBoxList(list);
    if (isUpdateMode) {
      const buybox = list.find((item) => item._id === selectedBuyBox._id);
      if (buybox) setSelectedBuyBox(buybox);
    } else {
      setSelectedBuyBox(list[0]);
    }
  }, [organization]);

  const getPropetyMatchByBuyBox = (buBox) => {
    dispatch(getBuyBoxMatchedProperties({ userId, buyBoxList: [buBox] }));
  };

  const onUpdateCriteria = (updatedBuyBox) => {
    const { addedAreas, removedAreas } = findAddedAndRemovedAreas(
      selectedBuyBox,
      updatedBuyBox,
    );
    const updatedBuyBoxList = buyBoxList.map((buyBox) =>
      buyBox._id === updatedBuyBox._id ? updatedBuyBox : buyBox,
    );
    setBuyBoxList(updatedBuyBoxList);

    if (variant === BUYBOX_VARIANT_ONBOARDING) {
      if (props.onChange) {
        props.onChange('buyBoxList', updatedBuyBoxList);
      }
    } else {
      saveOrganization({
        requestedUserId: userId,
        buyBoxList: updatedBuyBoxList,
        changedParams,
        addedAreas,
        removedAreas,
        buyBoxTitle: selectedBuyBox?.title || '',
      });
    }
    setIsUpdateMode(true);
  };

  const onDeleteCriteria = (criteria) => {
    const updatedBuyBoxList = buyBoxList.filter(
      (item) => criteria._id !== item._id,
    );
    setBuyBoxList(updatedBuyBoxList);
    saveOrganization({
      buyBoxList: updatedBuyBoxList,
      requestedUserId: userId,
    });
    setIsUpdateMode(false);

    const newPropertyMatches = { ...(buyBoxPropertyMatch || {}) };
    delete newPropertyMatches[criteria._id];
    dispatch(updateBuyBoxPropertyMatches(newPropertyMatches));
  };

  const onPreviewBuyBoxMatchProperties = (updatedBuyBox) => {
    getPropetyMatchByBuyBox(updatedBuyBox);
  };

  return (
    <Page>
      <ContentHeader title="Buy Box" />
      <Spin spinning={isLoading}>
        <div className="buybox-container">
          <BuyboxList
            buyBoxList={buyBoxList}
            onCreateCriteria={onCreateCriteria}
            selectedBuyBox={selectedBuyBox}
            setSelectedBuyBox={setSelectedBuyBox}
            isFetchingBuyboxMatchedProperties={
              isFetchingBuyboxMatchedProperties
            }
            buyBoxPropertyMatch={buyBoxPropertyMatch}
            isMatchingCountShown={isMatchingCountShown}
            hasUnsavedChanges={Object.keys(changedParams).length !== 0}
          />

          <div className="right">
            <AddBuyboxForm
              selectedBuyBox={selectedBuyBox}
              setSelectedBuyBox={setSelectedBuyBox}
              onUpdateCriteria={onUpdateCriteria}
              onDeleteCriteria={onDeleteCriteria}
              onPreviewBuyBoxMatchProperties={onPreviewBuyBoxMatchProperties}
              variant={variant}
              params={params}
              setParams={setParams}
              changedParams={changedParams}
              setChangedParams={setChangedParams}
              organizationMembers={organization.members}
              isMarketplace={isMarketplace}
            />
          </div>
        </div>
      </Spin>
      <WhiteLabelTrainingModal />
    </Page>
  );
};

export default BuyBoxPage;
